import DashboardLayout from '@/components/DashboardLayout';
import { router } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PhoneInput } from '@/components/ui/phone-input';
import { CpfInput } from '@/components/ui/cpf-input';
import {
  User, Mail, Phone, Calendar, ArrowLeft, ChevronLeft, ChevronRight,
  Edit2, Check, X, Receipt, BookOpen, DollarSign, UserCircle, Clock,
  CheckCircle2, CalendarClock, Plus, KeyRound, Save, Copy,
  Package, Users as UsersIcon, Wallet
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';

interface Student {
  id: number;
  name: string;
}

interface Lesson {
  id: number;
  lesson_date: string;
  start_time: string;
  end_time: string;
  student: Student;
  teacher_payment: string;
  description: string;
  status: string;
  attendance_status: string;
  is_paid: boolean;
}

interface UserData {
  id: number;
  name: string;
  email: string;
  phone: string;
  role: string;
  verified: boolean;
  bookable?: boolean;
  hourly_rate?: number;
  document?: string;
  created_at: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

interface Filters {
  start_date?: string;
  end_date?: string;
  status: string;
}

interface Stats {
  total_lessons?: number;
  total_payment?: number;
  paid_payment?: number;
  pending_payment?: number;
  // student stats
  total?: number;
  present?: number;
  absent?: number;
  pending?: number;
  cancelled?: number;
}

interface StudentLesson {
  id: number;
  lesson_date: string;
  start_time: string;
  end_time: string;
  teacher: { id: number; name: string } | null;
  status: string;
  attendance_status: string | null;
  checked_in_at: string | null;
  role: 'main' | 'extra';
  subscription: { id: number; package_name: string | null } | null;
  description: string | null;
}

interface SubscriptionData {
  id: number;
  package_name: string;
  lessons_total: number;
  lessons_remaining: number;
  lessons_used: number;
  status: string;
  purchased_at: string | null;
  expires_at: string | null;
  booking_deadline: string | null;
  final_price: number;
  discount_percentage: number;
  is_parent_subscription: boolean;
  owner_name: string;
  payment: {
    status: string;
    method: string;
    paid_at: string | null;
    amount: number;
  } | null;
}

interface PaymentData {
  id: number;
  amount: number;
  status: string;
  method: string;
  paid_at: string | null;
  created_at: string;
  package_name: string | null;
  subscription_id: number | null;
}

interface DependentData {
  id: number;
  name: string;
  email: string;
  phone: string;
  verified: boolean;
  has_active_package: boolean;
  active_package_name: string | null;
  lessons_remaining: number;
}

interface TeacherPaymentData {
  id: number;
  total_amount: number;
  formatted_total: string;
  status: string;
  payment_date: string | null;
  notes: string | null;
  lessons_count: number;
  created_at: string;
}

interface WaveCoinTransaction {
  id: number;
  type: string;
  amount: number;
  balance_after: number;
  description: string;
  created_at: string;
}

interface WaveCoinData {
  balance: number;
  balance_in_reais: number;
  transactions: WaveCoinTransaction[];
}

interface AvailabilitySlot {
  id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  max_capacity: number;
  day_name: string;
}

interface Props {
  user: UserData;
  lessons: Lesson[];
  pagination: Pagination;
  filters: Filters;
  stats: Stats;
  teacher_payments?: TeacherPaymentData[];
  wavecoin_data?: WaveCoinData | null;
  availability_slots?: AvailabilitySlot[];
  subscriptions?: SubscriptionData[];
  payments?: PaymentData[];
  dependents?: DependentData[];
  student_lessons?: StudentLesson[];
}

const ROLE_MAP: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  owner: { label: 'Dono', variant: 'default' },
  teacher: { label: 'Instrutor', variant: 'secondary' },
  student: { label: 'Aluno', variant: 'outline' },
};

const STATUS_MAP: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  scheduled: { label: 'Agendada', variant: 'secondary' },
  completed: { label: 'Concluida', variant: 'default' },
  cancelled: { label: 'Cancelada', variant: 'destructive' },
};

const PAYMENT_STATUS_MAP: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  pending: { label: 'Pendente', variant: 'secondary' },
  paid: { label: 'Pago', variant: 'default' },
  cancelled: { label: 'Cancelado', variant: 'destructive' },
};

const ATTENDANCE_MAP: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  present: { label: 'Presente', variant: 'default' },
  absent: { label: 'Ausente', variant: 'destructive' },
  pending: { label: 'Pendente', variant: 'secondary' },
};

const SUBSCRIPTION_STATUS_MAP: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  active: { label: 'Ativo', variant: 'default' },
  completed: { label: 'Finalizado', variant: 'secondary' },
  cancelled: { label: 'Cancelado', variant: 'destructive' },
  expired: { label: 'Vencido', variant: 'destructive' },
};

const PAYMENT_METHOD_MAP: Record<string, string> = {
  cash: 'Dinheiro',
  credit_card: 'Cartão de crédito',
  debit_card: 'Cartão de débito',
  pix: 'PIX',
  bank_transfer: 'Transferência',
};

const TRANSACTION_TYPE_MAP: Record<string, { label: string; color: string }> = {
  credit: { label: 'Credito', color: 'text-green-600' },
  debit: { label: 'Debito', color: 'text-red-600' },
  expiry: { label: 'Expirado', color: 'text-amber-600' },
  reversal: { label: 'Estorno', color: 'text-blue-600' },
};

const formatCurrency = (value: number): string => {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const DAY_NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

// Horários selecionáveis para a grade do professor, em passos de 30 min
// (05:00, 05:30, 06:00, ... 19:30). Permite grade começando na meia hora.
const HOUR_OPTIONS = Array.from({ length: 15 }, (_, i) => i + 5).flatMap((hour) => [
  `${String(hour).padStart(2, '0')}:00`,
  `${String(hour).padStart(2, '0')}:30`,
]);

export default function Show({ user, lessons, pagination, filters, stats, teacher_payments = [], wavecoin_data, availability_slots = [], subscriptions = [], payments = [], dependents = [], student_lessons = [] }: Props) {
  const [startDate, setStartDate] = useState(filters.start_date || '');
  const [endDate, setEndDate] = useState(filters.end_date || '');
  const [statusFilter, setStatusFilter] = useState(filters.status);
  const [editingLesson, setEditingLesson] = useState<number | null>(null);
  const [editingPayment, setEditingPayment] = useState<string>('');
  const [selectedLessons, setSelectedLessons] = useState<number[]>([]);
  const [generatePaymentDialogOpen, setGeneratePaymentDialogOpen] = useState(false);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [newSlotDays, setNewSlotDays] = useState<number[]>([1]);
  const [newSlotStart, setNewSlotStart] = useState('07:00');
  const [newSlotCapacity, setNewSlotCapacity] = useState('1');
  const [copyingFromDay, setCopyingFromDay] = useState<number | null>(null);
  const [copyTargetDays, setCopyTargetDays] = useState<number[]>([]);
  const [slotSelectionMode, setSlotSelectionMode] = useState(false);
  const [selectedSlotIds, setSelectedSlotIds] = useState<number[]>([]);

  const toggleSlotSelection = (slotId: number) => {
    setSelectedSlotIds(prev =>
      prev.includes(slotId) ? prev.filter(id => id !== slotId) : [...prev, slotId]
    );
  };

  const exitSelectionMode = () => {
    setSlotSelectionMode(false);
    setSelectedSlotIds([]);
  };

  const bulkDeleteSlots = () => {
    if (selectedSlotIds.length === 0) return;
    router.delete(`/users/${user.id}/availability_slots/bulk`, {
      data: { slot_ids: selectedSlotIds },
      preserveScroll: true,
      onSuccess: () => {
        toast.success(`${selectedSlotIds.length} horário(s) removido(s)`);
        exitSelectionMode();
      },
      onError: () => toast.error('Erro ao remover horários'),
    });
  };

  const toggleDay = (day: number) => {
    setNewSlotDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const toggleCopyTarget = (day: number) => {
    setCopyTargetDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  // Hourly rate state
  const [hourlyRateDisplay, setHourlyRateDisplay] = useState(
    user.hourly_rate && user.hourly_rate > 0 ? formatCurrency(user.hourly_rate) : ''
  );

  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileName, setProfileName] = useState(user.name);
  const [profileEmail, setProfileEmail] = useState(user.email);
  const [profilePhone, setProfilePhone] = useState(user.phone);
  const [profileDocument, setProfileDocument] = useState(user.document || '');
  const [profileRole, setProfileRole] = useState(user.role);
  const [savingProfile, setSavingProfile] = useState(false);

  // Password state
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const handleSaveProfile = () => {
    setSavingProfile(true);
    router.patch(`/users/${user.id}`, {
      user: { name: profileName, email: profileEmail, phone: profilePhone, document: profileDocument, role: profileRole }
    }, {
      preserveScroll: true,
      onSuccess: () => {
        toast.success('Perfil atualizado com sucesso!');
        setEditingProfile(false);
        setSavingProfile(false);
      },
      onError: (errors) => {
        toast.error(Object.values(errors).flat().join(', ') || 'Erro ao atualizar perfil');
        setSavingProfile(false);
      },
    });
  };

  const handleSavePassword = () => {
    if (password.length < 8) {
      toast.error('Senha deve ter no mínimo 8 caracteres');
      return;
    }
    if (password !== passwordConfirmation) {
      toast.error('As senhas não coincidem');
      return;
    }
    setSavingPassword(true);
    router.patch(`/users/${user.id}`, {
      user: { password, password_confirmation: passwordConfirmation }
    }, {
      preserveScroll: true,
      onSuccess: () => {
        toast.success('Senha alterada com sucesso!');
        setPassword('');
        setPasswordConfirmation('');
        setSavingPassword(false);
      },
      onError: (errors) => {
        toast.error(Object.values(errors).flat().join(', ') || 'Erro ao alterar senha');
        setSavingPassword(false);
      },
    });
  };

  const slotsByDay = availability_slots.reduce((acc, slot) => {
    const day = slot.day_of_week;
    if (!acc[day]) acc[day] = [];
    acc[day].push(slot);
    return acc;
  }, {} as Record<number, AvailabilitySlot[]>);

  const handleFilterChange = () => {
    router.get(`/users/${user.id}`, {
      start_date: startDate,
      end_date: endDate,
      status: statusFilter,
    }, { preserveState: true });
  };

  const handlePageChange = (page: number) => {
    router.get(`/users/${user.id}`, {
      ...filters,
      page,
    }, { preserveState: true });
  };

  const updateAttendance = (lessonId: number, status: 'present' | 'absent' | 'pending') => {
    router.patch(`/users/${user.id}/lessons/${lessonId}/attendance`, {
      attendance_status: status,
    }, { preserveScroll: true });
  };

  const startEditPayment = (lesson: Lesson) => {
    setEditingLesson(lesson.id);
    setEditingPayment(lesson.teacher_payment || '0');
  };

  const cancelEdit = () => {
    setEditingLesson(null);
    setEditingPayment('');
  };

  const savePayment = (lessonId: number) => {
    router.patch(`/users/${user.id}/lessons/${lessonId}/payment`, {
      teacher_payment: editingPayment,
    }, {
      preserveState: false,
      onSuccess: () => {
        setEditingLesson(null);
        setEditingPayment('');
      },
    });
  };

  const handleToggleLesson = (lessonId: number) => {
    setSelectedLessons(prev =>
      prev.includes(lessonId)
        ? prev.filter(id => id !== lessonId)
        : [...prev, lessonId]
    );
  };

  const handleSelectAll = () => {
    const unpaidLessonIds = lessons.filter(l => !l.is_paid).map(l => l.id);
    if (selectedLessons.length === unpaidLessonIds.length) {
      setSelectedLessons([]);
    } else {
      setSelectedLessons(unpaidLessonIds);
    }
  };

  const handleGeneratePayment = () => {
    if (selectedLessons.length === 0) return;
    setGeneratePaymentDialogOpen(true);
  };

  const confirmGeneratePayment = () => {
    router.post('/teacher_payments', {
      lesson_ids: selectedLessons,
      notes: paymentNotes
    }, {
      onSuccess: () => {
        setGeneratePaymentDialogOpen(false);
        setSelectedLessons([]);
        setPaymentNotes('');
      }
    });
  };

  const calculateSelectedTotal = () => {
    return selectedLessons.reduce((total, lessonId) => {
      const lesson = lessons.find(l => l.id === lessonId);
      return total + (lesson ? parseFloat(lesson.teacher_payment || '0') : 0);
    }, 0);
  };

  const isTeacher = user.role === 'teacher';
  const backPath = user.role === 'student' ? '/students' : '/team';
  const initials = user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <DashboardLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => router.visit(backPath)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <Avatar className="h-12 w-12">
            <AvatarFallback className="text-lg font-semibold bg-primary/10 text-primary">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl font-bold">{user.name}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={ROLE_MAP[user.role]?.variant || 'default'}>
                {ROLE_MAP[user.role]?.label || user.role}
              </Badge>
              <Badge variant={user.verified ? 'default' : 'secondary'}>
                {user.verified ? 'Verificado' : 'Nao Verificado'}
              </Badge>
            </div>
          </div>
        </div>

        {isTeacher ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} orientation="vertical" className="flex gap-6">
            <TabsList variant="line" className="w-56 shrink-0 border-r pr-2">
              <TabsTrigger value="profile" className="justify-start gap-2 px-3 py-2.5">
                <UserCircle className="w-4 h-4" />
                Perfil
              </TabsTrigger>
              <TabsTrigger value="scheduling" className="justify-start gap-2 px-3 py-2.5">
                <CalendarClock className="w-4 h-4" />
                Agendamento
              </TabsTrigger>
              <TabsTrigger value="lessons" className="justify-start gap-2 px-3 py-2.5">
                <BookOpen className="w-4 h-4" />
                Aulas
                {(stats.total_lessons ?? 0) > 0 && (
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {stats.total_lessons}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="payments" className="justify-start gap-2 px-3 py-2.5">
                <DollarSign className="w-4 h-4" />
                Pagamentos
                {teacher_payments.length > 0 && (
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {teacher_payments.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="password" className="justify-start gap-2 px-3 py-2.5">
                <KeyRound className="w-4 h-4" />
                Senha
              </TabsTrigger>
            </TabsList>

            {/* Tab: Perfil */}
            <TabsContent value="profile" className="flex-1 space-y-6 mt-0">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Informações Pessoais
                    </CardTitle>
                    {!editingProfile && (
                      <Button variant="outline" size="sm" onClick={() => setEditingProfile(true)}>
                        <Edit2 className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {editingProfile ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="profileName">Nome Completo</Label>
                          <Input
                            id="profileName"
                            value={profileName}
                            onChange={(e) => setProfileName(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="profileEmail">Email</Label>
                          <Input
                            id="profileEmail"
                            type="email"
                            value={profileEmail}
                            onChange={(e) => setProfileEmail(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="profilePhone">Telefone</Label>
                          <PhoneInput
                            id="profilePhone"
                            value={profilePhone}
                            onChange={(e) => setProfilePhone(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="profileDocument">CPF</Label>
                          <CpfInput
                            id="profileDocument"
                            value={profileDocument}
                            onChange={(e) => setProfileDocument(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Função</Label>
                          <Select value={profileRole} onValueChange={setProfileRole}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="teacher">Professor</SelectItem>
                              <SelectItem value="manager">Gerente</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button onClick={handleSaveProfile} disabled={savingProfile}>
                          <Save className="w-4 h-4 mr-1" />
                          {savingProfile ? 'Salvando...' : 'Salvar'}
                        </Button>
                        <Button variant="outline" onClick={() => {
                          setEditingProfile(false);
                          setProfileName(user.name);
                          setProfileEmail(user.email);
                          setProfilePhone(user.phone);
                          setProfileDocument(user.document || '');
                          setProfileRole(user.role);
                        }}>
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <div className="text-sm text-muted-foreground mb-1">Nome</div>
                        <div className="font-medium text-lg">{user.name}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email
                        </div>
                        <div className="font-medium">{user.email}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          Telefone
                        </div>
                        <div className="font-medium">{user.phone}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground mb-1">CPF</div>
                        <div className="font-medium">
                          {user.document ? user.document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : (
                            <span className="text-amber-600 text-sm">Não informado</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Data de Cadastro
                        </div>
                        <div className="font-medium">
                          {format(new Date(user.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Resumo */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-500/10">
                        <BookOpen className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Total de Aulas</div>
                        <div className="text-2xl font-bold">{stats.total_lessons || 0}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-500/10">
                        <DollarSign className="w-5 h-5 text-green-500" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Total Ganho</div>
                        <div className="text-2xl font-bold">R$ {(stats.total_payment || 0).toFixed(2)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-emerald-500/10">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Total Pago</div>
                        <div className="text-2xl font-bold text-emerald-600">R$ {(stats.paid_payment || 0).toFixed(2)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-amber-500/10">
                        <Clock className="w-5 h-5 text-amber-500" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Pendente</div>
                        <div className="text-2xl font-bold text-amber-600">R$ {(stats.pending_payment || 0).toFixed(2)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Tab: Agendamento */}
            <TabsContent value="scheduling" className="flex-1 space-y-6 mt-0">
              {/* Valor da Aula + Bookable */}
              <Card>
                <CardHeader>
                  <CardTitle>Configurações de Agendamento</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Valor da Aula */}
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium">Valor da Aula</div>
                      <p className="text-sm text-muted-foreground">
                        Valor padrão para cada aula deste professor
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">R$</span>
                      <Input
                        type="text"
                        inputMode="decimal"
                        className="w-32 text-right"
                        placeholder="0,00"
                        value={hourlyRateDisplay}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/\D/g, '');
                          if (raw === '') {
                            setHourlyRateDisplay('');
                            return;
                          }
                          const cents = parseInt(raw, 10);
                          setHourlyRateDisplay(formatCurrency(cents / 100));
                        }}
                      />
                      <Button
                        size="sm"
                        onClick={() => {
                          const raw = hourlyRateDisplay.replace(/\D/g, '');
                          const val = parseInt(raw || '0', 10) / 100;
                          router.patch(`/users/${user.id}`, { user: { hourly_rate: val } }, {
                            preserveScroll: true,
                            onSuccess: () => toast.success(`Valor da aula: R$ ${formatCurrency(val)}`),
                          });
                        }}
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Bookable Toggle */}
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium">Disponível para agendamento pelo app</div>
                      <p className="text-sm text-muted-foreground">
                        {user.bookable !== false
                          ? 'Alunos podem agendar aulas com este professor pelo app'
                          : 'Somente o admin pode associar alunos a este professor'
                        }
                      </p>
                    </div>
                    <Switch
                      checked={user.bookable !== false}
                      onCheckedChange={(checked) => {
                        router.patch(`/users/${user.id}`, { user: { bookable: checked } }, {
                          preserveScroll: true,
                          onSuccess: () => {
                            toast.success(checked
                              ? 'Professor disponível para agendamento pelo app'
                              : 'Professor restrito ao agendamento pelo admin'
                            );
                          },
                        });
                      }}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Grade de Horários */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle>Grade de Horários</CardTitle>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {availability_slots.length} {availability_slots.length === 1 ? 'horário' : 'horários'}
                      </Badge>
                      {slotSelectionMode ? (
                        <>
                          <span className="text-xs text-muted-foreground">
                            {selectedSlotIds.length} selecionado(s)
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedSlotIds(availability_slots.map(s => s.id))}
                          >
                            Selecionar tudo
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={selectedSlotIds.length === 0}
                            onClick={bulkDeleteSlots}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Excluir ({selectedSlotIds.length})
                          </Button>
                          <Button size="sm" variant="ghost" onClick={exitSelectionMode}>
                            Cancelar
                          </Button>
                        </>
                      ) : (
                        <>
                          {availability_slots.length > 0 && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setSlotSelectionMode(true)}
                              >
                                Selecionar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  router.post(`/users/${user.id}/generate_lessons`, {}, {
                                    preserveScroll: true,
                                    onSuccess: () => toast.success('Aulas geradas com sucesso!'),
                                  });
                                }}
                              >
                                <CalendarClock className="h-4 w-4 mr-1" />
                                Gerar Aulas
                              </Button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Form para adicionar com multi-select de dias */}
                  <div className="space-y-3">
                    <div className="flex items-end gap-3 flex-wrap">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Hora Inicial</Label>
                        <Select value={newSlotStart} onValueChange={setNewSlotStart}>
                          <SelectTrigger className="w-[110px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {HOUR_OPTIONS.map((h) => (
                              <SelectItem key={h} value={h}>{h}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Vagas</Label>
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="w-[90px] text-center"
                          value={newSlotCapacity}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, '');
                            setNewSlotCapacity(raw);
                          }}
                          placeholder="1"
                        />
                      </div>
                      <Button
                        variant="default"
                        disabled={newSlotDays.length === 0}
                        onClick={() => {
                          // Fim = início + 1h, preservando os minutos (05:30 → 06:30)
                          const [startH, startM] = newSlotStart.split(':').map(Number);
                          const endTotal = startH * 60 + startM + 60;
                          const endTime = `${String(Math.floor(endTotal / 60) % 24).padStart(2, '0')}:${String(endTotal % 60).padStart(2, '0')}`;
                          router.post(`/users/${user.id}/availability_slots`, {
                            days_of_week: newSlotDays,
                            start_time: newSlotStart,
                            end_time: endTime,
                            max_capacity: parseInt(newSlotCapacity),
                          }, {
                            preserveScroll: true,
                            onSuccess: () => toast.success('Horários adicionados!'),
                            onError: () => toast.error('Erro ao adicionar horário'),
                          });
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Adicionar
                      </Button>
                    </div>
                    <div className="flex items-center gap-1">
                      <Label className="text-xs text-muted-foreground mr-2">Dias:</Label>
                      {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => toggleDay(day)}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                            newSlotDays.includes(day)
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                        >
                          {DAY_NAMES[day].substring(0, 3)}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          if (newSlotDays.length === 7) {
                            setNewSlotDays([]);
                          } else {
                            setNewSlotDays([1, 2, 3, 4, 5, 6, 0]);
                          }
                        }}
                        className="ml-1 px-2 py-1 rounded-md text-xs text-muted-foreground hover:text-foreground transition-colors underline"
                      >
                        {newSlotDays.length === 7 ? 'Limpar' : 'Todos'}
                      </button>
                    </div>
                  </div>

                  {/* Grid semanal */}
                  {availability_slots.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CalendarClock className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p className="text-sm">Nenhum horário configurado</p>
                      <p className="text-xs mt-1">Selecione os dias, hora e clique em Adicionar</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-7 gap-2">
                      {/* Header dos dias com botão copiar */}
                      {[1, 2, 3, 4, 5, 6, 0].map((day) => {
                        const hasSlots = (slotsByDay[day] || []).length > 0;
                        return (
                          <div key={`header-${day}`} className="text-center pb-2 border-b">
                            <div className="text-xs font-semibold text-muted-foreground">{DAY_NAMES[day].substring(0, 3)}</div>
                            {hasSlots && (
                              <button
                                type="button"
                                onClick={() => {
                                  setCopyingFromDay(copyingFromDay === day ? null : day);
                                  setCopyTargetDays([]);
                                }}
                                className={`mt-1 inline-flex items-center gap-0.5 text-[10px] transition-colors ${
                                  copyingFromDay === day
                                    ? 'text-primary font-medium'
                                    : 'text-muted-foreground hover:text-foreground'
                                }`}
                              >
                                <Copy className="h-2.5 w-2.5" />
                                Copiar
                              </button>
                            )}
                          </div>
                        );
                      })}

                      {/* Copiar dia: seletor de destino */}
                      {copyingFromDay !== null && (
                        <div className="col-span-7 flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
                          <span className="text-xs font-medium">
                            Copiar {DAY_NAMES[copyingFromDay]} para:
                          </span>
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5, 6, 0].filter(d => d !== copyingFromDay).map((day) => (
                              <button
                                key={day}
                                type="button"
                                onClick={() => toggleCopyTarget(day)}
                                className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                                  copyTargetDays.includes(day)
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                                }`}
                              >
                                {DAY_NAMES[day].substring(0, 3)}
                              </button>
                            ))}
                          </div>
                          <Button
                            size="sm"
                            variant="default"
                            className="ml-auto h-7 text-xs"
                            disabled={copyTargetDays.length === 0}
                            onClick={() => {
                              router.post(`/users/${user.id}/availability_slots/copy_day`, {
                                source_day: copyingFromDay,
                                target_days: copyTargetDays,
                              }, {
                                preserveScroll: true,
                                onSuccess: () => {
                                  toast.success('Horários copiados!');
                                  setCopyingFromDay(null);
                                  setCopyTargetDays([]);
                                },
                              });
                            }}
                          >
                            Aplicar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs"
                            onClick={() => {
                              setCopyingFromDay(null);
                              setCopyTargetDays([]);
                            }}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}

                      {/* Slots por dia */}
                      {[1, 2, 3, 4, 5, 6, 0].map((day) => {
                        const slots = (slotsByDay[day] || []).sort((a, b) => a.start_time.localeCompare(b.start_time));
                        return (
                          <div key={`col-${day}`} className="space-y-1.5 min-h-[60px]">
                            {slots.map((slot) => {
                              const start = slot.start_time.includes('T')
                                ? slot.start_time.substring(11, 16)
                                : slot.start_time.substring(0, 5);
                              const end = slot.end_time.includes('T')
                                ? slot.end_time.substring(11, 16)
                                : slot.end_time.substring(0, 5);
                              const isSelected = selectedSlotIds.includes(slot.id);
                              return (
                                <div
                                  key={slot.id}
                                  onClick={() => slotSelectionMode && toggleSlotSelection(slot.id)}
                                  className={`group relative rounded-md border px-2 py-1.5 text-center transition-colors ${
                                    slotSelectionMode
                                      ? `cursor-pointer ${isSelected ? 'border-primary bg-primary/10' : 'bg-muted/40 hover:bg-muted/70'}`
                                      : 'bg-muted/40 hover:bg-muted/70'
                                  }`}
                                >
                                  <div className="text-xs font-medium">{start} - {end}</div>
                                  <div className="text-[10px] text-muted-foreground">
                                    {slot.max_capacity} {slot.max_capacity === 1 ? 'vaga' : 'vagas'}
                                  </div>
                                  {slotSelectionMode && isSelected && (
                                    <div className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                                      <Check className="h-2.5 w-2.5" />
                                    </div>
                                  )}
                                  {!slotSelectionMode && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        router.delete(`/users/${user.id}/availability_slots/${slot.id}`, {
                                          preserveScroll: true,
                                          onSuccess: () => toast.success('Horário removido'),
                                        });
                                      }}
                                      className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground opacity-60 group-hover:opacity-100 hover:scale-110 transition-all"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Aulas */}
            <TabsContent value="lessons" className="flex-1 space-y-4 mt-0">
              {/* Filtros */}
              <Card>
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">Data Inicio</label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Data Fim</label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Status</label>
                      <select
                        className="w-full border rounded-md px-3 py-2 bg-background text-foreground border-input focus:ring-2 focus:ring-ring focus:outline-none h-9 text-sm"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="all">Todos</option>
                        <option value="scheduled">Agendada</option>
                        <option value="completed">Concluida</option>
                        <option value="cancelled">Cancelada</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <Button onClick={handleFilterChange} className="w-full">
                        Filtrar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Acoes em massa */}
              {selectedLessons.length > 0 && (
                <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-lg px-4 py-3">
                  <span className="text-sm font-medium">
                    {selectedLessons.length} {selectedLessons.length === 1 ? 'aula selecionada' : 'aulas selecionadas'}
                    {' '}&middot;{' '}
                    <span className="text-green-600 font-semibold">R$ {calculateSelectedTotal().toFixed(2)}</span>
                  </span>
                  <Button size="sm" onClick={handleGeneratePayment}>
                    <Receipt className="w-4 h-4 mr-2" />
                    Gerar Pagamento
                  </Button>
                </div>
              )}

              {/* Tabela de Aulas */}
              <Card>
                <CardContent className="p-0">
                  {lessons.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Nenhuma aula encontrada</p>
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-12">
                                <Checkbox
                                  checked={selectedLessons.length > 0 && selectedLessons.length === lessons.filter(l => !l.is_paid).length}
                                  onCheckedChange={handleSelectAll}
                                />
                              </TableHead>
                              <TableHead>Data</TableHead>
                              <TableHead>Horario</TableHead>
                              <TableHead>Aluno</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Pagamento</TableHead>
                              <TableHead className="text-right">Acoes</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {lessons.map((lesson) => {
                              const statusInfo = STATUS_MAP[lesson.status] || { label: lesson.status, variant: 'outline' as const };
                              return (
                                <TableRow key={lesson.id}>
                                  <TableCell>
                                    <Checkbox
                                      checked={selectedLessons.includes(lesson.id)}
                                      onCheckedChange={() => handleToggleLesson(lesson.id)}
                                      disabled={lesson.is_paid}
                                    />
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap">
                                    {format(new Date(lesson.lesson_date + 'T00:00:00'), "dd 'de' MMM, yyyy", { locale: ptBR })}
                                  </TableCell>
                                  <TableCell>
                                    <span className="text-sm font-mono">{lesson.start_time} - {lesson.end_time}</span>
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    {lesson.student?.name || '-'}
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                                  </TableCell>
                                  <TableCell>
                                    {editingLesson === lesson.id ? (
                                      <div className="flex items-center gap-2">
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={editingPayment}
                                          onChange={(e) => setEditingPayment(e.target.value)}
                                          className="w-24"
                                        />
                                        <Button size="icon" variant="ghost" onClick={() => savePayment(lesson.id)}>
                                          <Check className="w-4 h-4 text-green-600" />
                                        </Button>
                                        <Button size="icon" variant="ghost" onClick={cancelEdit}>
                                          <X className="w-4 h-4 text-destructive" />
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-2">
                                        {lesson.teacher_payment && parseFloat(lesson.teacher_payment) > 0 ? (
                                          <>
                                            <span className="font-semibold text-green-700">
                                              R$ {parseFloat(lesson.teacher_payment).toFixed(2)}
                                            </span>
                                            {lesson.is_paid ? (
                                              <Badge variant="default" className="bg-green-600 text-xs">Paga</Badge>
                                            ) : (
                                              <Badge variant="secondary" className="text-xs">Pendente</Badge>
                                            )}
                                          </>
                                        ) : (
                                          <span className="text-muted-foreground text-sm">-</span>
                                        )}
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {editingLesson !== lesson.id && !lesson.is_paid && (
                                      <Button variant="ghost" size="icon" onClick={() => startEditPayment(lesson)}>
                                        <Edit2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>

                      {/* Paginacao */}
                      {pagination.total_pages > 1 && (
                        <div className="flex items-center justify-between p-4 border-t">
                          <div className="text-sm text-muted-foreground">
                            Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a{' '}
                            {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} de{' '}
                            {pagination.total_count} aulas
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handlePageChange(pagination.current_page - 1)}
                              disabled={pagination.current_page === 1}
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <div className="text-sm">
                              Pagina {pagination.current_page} de {pagination.total_pages}
                            </div>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handlePageChange(pagination.current_page + 1)}
                              disabled={pagination.current_page === pagination.total_pages}
                            >
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Pagamentos */}
            <TabsContent value="payments" className="flex-1 space-y-4 mt-0">
              {/* Resumo de pagamentos */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-500/10">
                        <DollarSign className="w-5 h-5 text-green-500" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Total Ganho</div>
                        <div className="text-xl font-bold">R$ {(stats.total_payment || 0).toFixed(2)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-emerald-500/10">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Total Pago</div>
                        <div className="text-xl font-bold text-emerald-600">R$ {(stats.paid_payment || 0).toFixed(2)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-amber-500/10">
                        <Clock className="w-5 h-5 text-amber-500" />
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Pendente</div>
                        <div className="text-xl font-bold text-amber-600">R$ {(stats.pending_payment || 0).toFixed(2)}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Lista de pagamentos */}
              <Card>
                <CardContent className="p-0">
                  {teacher_payments.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Nenhum pagamento registrado</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data de Criacao</TableHead>
                          <TableHead>Aulas</TableHead>
                          <TableHead>Valor</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Data Pagamento</TableHead>
                          <TableHead>Observacoes</TableHead>
                          <TableHead className="text-right">Acoes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {teacher_payments.map((payment) => {
                          const statusInfo = PAYMENT_STATUS_MAP[payment.status] || { label: payment.status, variant: 'outline' as const };
                          return (
                            <TableRow key={payment.id}>
                              <TableCell className="whitespace-nowrap">
                                {format(new Date(payment.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">{payment.lessons_count} {payment.lessons_count === 1 ? 'aula' : 'aulas'}</Badge>
                              </TableCell>
                              <TableCell>
                                <span className="font-semibold">{payment.formatted_total}</span>
                              </TableCell>
                              <TableCell>
                                <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
                              </TableCell>
                              <TableCell>
                                {payment.payment_date
                                  ? format(new Date(payment.payment_date + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR })
                                  : <span className="text-muted-foreground">-</span>
                                }
                              </TableCell>
                              <TableCell>
                                {payment.notes ? (
                                  <span className="text-sm text-muted-foreground line-clamp-1">{payment.notes}</span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => router.visit(`/teacher_payments/${payment.id}`)}
                                >
                                  Ver detalhes
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Tab: Senha */}
            <TabsContent value="password" className="flex-1 space-y-6 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <KeyRound className="w-5 h-5" />
                    Alterar Senha
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-w-md">
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">Nova Senha</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        placeholder="Mínimo 8 caracteres"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="Repita a nova senha"
                        value={passwordConfirmation}
                        onChange={(e) => setPasswordConfirmation(e.target.value)}
                      />
                    </div>
                    <Button
                      onClick={handleSavePassword}
                      disabled={savingPassword || !password}
                    >
                      <Save className="w-4 h-4 mr-1" />
                      {savingPassword ? 'Salvando...' : 'Alterar Senha'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : (
          /* Layout para alunos com tabs */
          <Tabs value={activeTab} onValueChange={setActiveTab} orientation="vertical" className="flex gap-6">
            <TabsList variant="line" className="w-56 shrink-0 border-r pr-2">
              <TabsTrigger value="profile" className="justify-start gap-2 px-3 py-2.5">
                <UserCircle className="w-4 h-4" />
                Perfil
              </TabsTrigger>
              <TabsTrigger value="subscriptions" className="justify-start gap-2 px-3 py-2.5">
                <Package className="w-4 h-4" />
                Pacotes
                {subscriptions.length > 0 && (
                  <Badge variant="secondary" className="ml-auto text-xs">{subscriptions.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="lessons" className="justify-start gap-2 px-3 py-2.5">
                <BookOpen className="w-4 h-4" />
                Aulas
                {(stats.total ?? 0) > 0 && (
                  <Badge variant="secondary" className="ml-auto text-xs">{stats.total}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="payments" className="justify-start gap-2 px-3 py-2.5">
                <Receipt className="w-4 h-4" />
                Pagamentos
                {payments.length > 0 && (
                  <Badge variant="secondary" className="ml-auto text-xs">{payments.length}</Badge>
                )}
              </TabsTrigger>
              {dependents.length > 0 && (
                <TabsTrigger value="dependents" className="justify-start gap-2 px-3 py-2.5">
                  <UsersIcon className="w-4 h-4" />
                  Dependentes
                  <Badge variant="secondary" className="ml-auto text-xs">{dependents.length}</Badge>
                </TabsTrigger>
              )}
              {wavecoin_data && (
                <TabsTrigger value="wavecoins" className="justify-start gap-2 px-3 py-2.5">
                  <Wallet className="w-4 h-4" />
                  WaveCoins
                </TabsTrigger>
              )}
              <TabsTrigger value="password" className="justify-start gap-2 px-3 py-2.5">
                <KeyRound className="w-4 h-4" />
                Senha
              </TabsTrigger>
            </TabsList>

            {/* Aba: Perfil */}
            <TabsContent value="profile" className="flex-1 space-y-6 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Informações Pessoais
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Nome</div>
                      <div className="font-medium text-lg">{user.name}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email
                      </div>
                      <div className="font-medium">{user.email}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        Telefone
                      </div>
                      <div className="font-medium">{user.phone}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">CPF</div>
                      <div className="font-medium">
                        {user.document ? user.document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : (
                          <span className="text-amber-600 text-sm">Não informado</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Data de Cadastro
                      </div>
                      <div className="font-medium">
                        {format(new Date(user.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Aba: Pacotes */}
            <TabsContent value="subscriptions" className="flex-1 space-y-4 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Pacotes do aluno
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {subscriptions.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      Esse aluno ainda não possui nenhum pacote.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Pacote</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Aulas</TableHead>
                          <TableHead>Compra</TableHead>
                          <TableHead>Vencimento</TableHead>
                          <TableHead className="text-right">Valor</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {subscriptions.map((s) => {
                          const subStatus = SUBSCRIPTION_STATUS_MAP[s.status] || { label: s.status, variant: 'outline' as const };
                          return (
                            <TableRow key={s.id}>
                              <TableCell>
                                <div className="font-medium">{s.package_name}</div>
                                {s.is_parent_subscription && (
                                  <div className="text-xs text-muted-foreground">de {s.owner_name}</div>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant={subStatus.variant}>{subStatus.label}</Badge>
                              </TableCell>
                              <TableCell>
                                <div className="font-medium">{s.lessons_used} / {s.lessons_total}</div>
                                <div className="text-xs text-muted-foreground">{s.lessons_remaining} restantes</div>
                              </TableCell>
                              <TableCell>
                                {s.purchased_at ? format(new Date(s.purchased_at), 'dd/MM/yyyy', { locale: ptBR }) : '-'}
                              </TableCell>
                              <TableCell>
                                {s.expires_at ? format(new Date(s.expires_at), 'dd/MM/yyyy', { locale: ptBR }) : (
                                  <span className="text-muted-foreground text-sm">Sem vencimento</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="font-medium">R$ {formatCurrency(s.final_price)}</div>
                                {s.discount_percentage > 0 && (
                                  <div className="text-xs text-green-600">{s.discount_percentage}% off</div>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Aba: Aulas */}
            <TabsContent value="lessons" className="flex-1 space-y-4 mt-0">
              {/* Stats */}
              {(stats.total ?? 0) > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Total</div><div className="text-2xl font-bold">{stats.total}</div></CardContent></Card>
                  <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Presentes</div><div className="text-2xl font-bold text-green-600">{stats.present}</div></CardContent></Card>
                  <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Ausentes</div><div className="text-2xl font-bold text-red-600">{stats.absent}</div></CardContent></Card>
                  <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Pendentes</div><div className="text-2xl font-bold text-amber-600">{stats.pending}</div></CardContent></Card>
                  <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Canceladas</div><div className="text-2xl font-bold text-muted-foreground">{stats.cancelled}</div></CardContent></Card>
                </div>
              )}

              {/* Filtros */}
              <Card>
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div className="space-y-1">
                      <Label htmlFor="lesson_start_date" className="text-xs">De</Label>
                      <Input id="lesson_start_date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="lesson_end_date" className="text-xs">Até</Label>
                      <Input id="lesson_end_date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Status</Label>
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="scheduled">Agendada</SelectItem>
                          <SelectItem value="completed">Concluída</SelectItem>
                          <SelectItem value="cancelled">Cancelada</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={() => {
                        router.get(`/users/${user.id}`, {
                          start_date: startDate,
                          end_date: endDate,
                          status: statusFilter,
                          page: 1,
                        }, { preserveState: true });
                      }}
                    >
                      Filtrar
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Tabela */}
              <Card>
                <CardContent className="p-0">
                  {student_lessons.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      Nenhuma aula encontrada com os filtros aplicados.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Horário</TableHead>
                          <TableHead>Professor</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Presença</TableHead>
                          <TableHead>Pacote</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {student_lessons.map((l) => {
                          const lessonStatus = STATUS_MAP[l.status] || { label: l.status, variant: 'outline' as const };
                          const att = l.attendance_status ? (ATTENDANCE_MAP[l.attendance_status] || { label: l.attendance_status, variant: 'outline' as const }) : null;
                          const isCancelled = l.status === 'cancelled';
                          const hasAttendance = l.attendance_status === 'present' || l.attendance_status === 'absent';
                          return (
                            <TableRow key={l.id}>
                              <TableCell>
                                <div className="font-medium">{format(new Date(l.lesson_date + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR })}</div>
                                {l.role === 'extra' && <div className="text-xs text-muted-foreground">aluno extra</div>}
                              </TableCell>
                              <TableCell className="text-sm">{l.start_time} - {l.end_time}</TableCell>
                              <TableCell className="text-sm">{l.teacher?.name || '-'}</TableCell>
                              <TableCell><Badge variant={lessonStatus.variant}>{lessonStatus.label}</Badge></TableCell>
                              <TableCell>
                                {isCancelled ? <span className="text-xs text-muted-foreground">-</span> :
                                  att ? <Badge variant={att.variant}>{att.label}</Badge> : <span className="text-xs text-muted-foreground">-</span>}
                              </TableCell>
                              <TableCell className="text-sm">{l.subscription?.package_name || <span className="text-muted-foreground">não vinculado</span>}</TableCell>
                              <TableCell className="text-right">
                                {isCancelled ? (
                                  <span className="text-xs text-muted-foreground">—</span>
                                ) : hasAttendance ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => updateAttendance(l.id, 'pending')}
                                  >
                                    <X className="w-3 h-3 mr-1" />
                                    Resetar
                                  </Button>
                                ) : (
                                  <div className="flex justify-end gap-1">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs text-green-700 hover:bg-green-50 hover:text-green-800"
                                      onClick={() => updateAttendance(l.id, 'present')}
                                    >
                                      <Check className="w-3 h-3 mr-1" />
                                      Presente
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 text-xs text-red-700 hover:bg-red-50 hover:text-red-800"
                                      onClick={() => updateAttendance(l.id, 'absent')}
                                    >
                                      <X className="w-3 h-3 mr-1" />
                                      Ausente
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}

                  {/* Paginação */}
                  {pagination.total_pages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                      <div className="text-xs text-muted-foreground">
                        Página {pagination.current_page} de {pagination.total_pages} · {pagination.total_count} aulas
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={pagination.current_page === 1}
                          onClick={() => handlePageChange(pagination.current_page - 1)}>
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" disabled={pagination.current_page === pagination.total_pages}
                          onClick={() => handlePageChange(pagination.current_page + 1)}>
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Aba: Pagamentos */}
            <TabsContent value="payments" className="flex-1 space-y-4 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="w-5 h-5" />
                    Pagamentos do aluno
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {payments.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      Nenhum pagamento registrado.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Pacote</TableHead>
                          <TableHead>Valor</TableHead>
                          <TableHead>Forma</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Data</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payments.map((p) => {
                          const payStatus = PAYMENT_STATUS_MAP[p.status] || { label: p.status, variant: 'outline' as const };
                          return (
                            <TableRow key={p.id}>
                              <TableCell className="font-medium">{p.package_name || '-'}</TableCell>
                              <TableCell>R$ {formatCurrency(p.amount)}</TableCell>
                              <TableCell className="text-sm">{PAYMENT_METHOD_MAP[p.method] || p.method}</TableCell>
                              <TableCell><Badge variant={payStatus.variant}>{payStatus.label}</Badge></TableCell>
                              <TableCell className="text-sm">
                                {p.paid_at ? format(new Date(p.paid_at), 'dd/MM/yyyy', { locale: ptBR }) :
                                  format(new Date(p.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Aba: Dependentes */}
            {dependents.length > 0 && (
              <TabsContent value="dependents" className="flex-1 space-y-4 mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <UsersIcon className="w-5 h-5" />
                      Dependentes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Telefone</TableHead>
                          <TableHead>Pacote</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dependents.map((d) => (
                          <TableRow key={d.id}>
                            <TableCell className="font-medium">{d.name}</TableCell>
                            <TableCell className="text-sm">{d.email}</TableCell>
                            <TableCell className="text-sm">{d.phone}</TableCell>
                            <TableCell className="text-sm">
                              {d.has_active_package ? (
                                <div>
                                  <div>{d.active_package_name}</div>
                                  <div className="text-xs text-muted-foreground">{d.lessons_remaining} aulas restantes</div>
                                </div>
                              ) : (
                                <span className="text-muted-foreground">Sem pacote</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="outline" size="sm" onClick={() => router.visit(`/users/${d.id}`)}>
                                Ver detalhes
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Aba: WaveCoins */}
            {wavecoin_data && (
              <TabsContent value="wavecoins" className="flex-1 space-y-4 mt-0">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Wallet className="w-5 h-5" />
                      WaveCoins
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-6">
                      <div>
                        <div className="text-sm text-muted-foreground">Saldo</div>
                        <div className="text-2xl font-bold text-primary">{wavecoin_data.balance} coins</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Equivalente</div>
                        <div className="text-lg font-medium text-green-600">R$ {wavecoin_data.balance_in_reais.toFixed(2)}</div>
                      </div>
                    </div>

                    {wavecoin_data.transactions.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2">Histórico recente</h4>
                        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                          {wavecoin_data.transactions.map((t) => {
                            const typeInfo = TRANSACTION_TYPE_MAP[t.type] || { label: t.type, color: '' };
                            return (
                              <div key={t.id} className="flex items-center justify-between text-sm border-b pb-2 last:border-0">
                                <div className="min-w-0">
                                  <span className={`font-medium ${typeInfo.color}`}>
                                    {t.type === 'credit' ? '+' : '-'}{t.amount}
                                  </span>
                                  <span className="text-muted-foreground ml-2">{t.description}</span>
                                </div>
                                <div className="text-right shrink-0 text-muted-foreground text-xs">
                                  <div>{t.created_at}</div>
                                  <div>Saldo: {t.balance_after}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}

            {/* Aba: Senha */}
            <TabsContent value="password" className="flex-1 space-y-6 mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <KeyRound className="w-5 h-5" />
                    Alterar Senha
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-w-md">
                    <div className="space-y-2">
                      <Label htmlFor="newPasswordStudent">Nova Senha</Label>
                      <Input
                        id="newPasswordStudent"
                        type="password"
                        placeholder="Mínimo 8 caracteres"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPasswordStudent">Confirmar Nova Senha</Label>
                      <Input
                        id="confirmPasswordStudent"
                        type="password"
                        placeholder="Repita a nova senha"
                        value={passwordConfirmation}
                        onChange={(e) => setPasswordConfirmation(e.target.value)}
                      />
                    </div>
                    <Button
                      onClick={handleSavePassword}
                      disabled={savingPassword || !password}
                    >
                      <Save className="w-4 h-4 mr-1" />
                      {savingPassword ? 'Salvando...' : 'Alterar Senha'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Dialog - Gerar Pagamento */}
      <AlertDialog open={generatePaymentDialogOpen} onOpenChange={setGeneratePaymentDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gerar Pagamento</AlertDialogTitle>
            <AlertDialogDescription>
              Voce esta gerando pagamento para {selectedLessons.length} {selectedLessons.length === 1 ? 'aula' : 'aulas'} do professor {user.name}.
              O total sera R$ {calculateSelectedTotal().toFixed(2)}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="notes">Observacoes (opcional)</Label>
            <Input
              id="notes"
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              placeholder="Ex: Pagamento referente ao mes de..."
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPaymentNotes('')}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmGeneratePayment}>
              Gerar Pagamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
